import { Link } from "react-router-dom";
import { Youtube, Instagram, Twitter } from "lucide-react";
import ShiSheetCta from "@/components/ShiSheetCta";
import { YOUTUBE_URL, PRESS_EMAIL, SOCIALS, SPOTIFY_URL } from "@/lib/images";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.5 2h3.1c.2 1.6 1.1 3 2.6 3.7.6.3 1.2.4 1.8.5v3.2c-1.5 0-3-.5-4.2-1.3v6.1c0 3.3-2.7 5.9-6 5.8-3-.1-5.4-2.6-5.3-5.6.1-2.9 2.5-5.2 5.4-5.2.3 0 .6 0 .9.1v3.3c-.3-.1-.6-.2-.9-.2-1.4 0-2.5 1.2-2.4 2.6.1 1.3 1.2 2.3 2.5 2.2 1.3 0 2.3-1.1 2.3-2.4V2z" />
  </svg>
);

const ThreadsIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M16.4 9.2c-.5-2.9-2.6-4.5-5.6-4.5-3.4 0-5.9 2.2-5.9 6.8 0 4.4 2.3 7 6.1 7 3.1 0 5.5-1.8 5.5-4.7 0-2.4-1.7-3.8-4.6-3.8-2.6 0-4.4 1.2-4.4 3.3 0 1.7 1.2 2.8 3.2 2.8 2.8 0 4.8-1.5 5.6-3.1" />
  </svg>
);

const socialCls =
  "flex h-10 w-10 items-center justify-center rounded-full border border-latte/60 text-latte transition-colors hover:border-gold hover:text-gold";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-corduroy bg-ink"
    >
      <ShiSheetCta />
      <div className="mx-auto max-w-7xl px-5 md:px-10 pt-20 pb-10">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-gold">
              The Shi Sheet
            </p>
            <h2 className="mt-4 font-editorial italic text-3xl md:text-4xl text-cream leading-tight">
              Keep up with SHi.
            </h2>
          </div>

          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-latte">
              The Show
            </p>
            <ul className="mt-5 space-y-4 text-[15px] leading-relaxed">
              <li>
                <Link to="/episodes" data-testid="footer-link-episodes" className="text-cream/80 transition-colors hover:text-gold">
                  Episode Archive
                </Link>
              </li>
              <li>
                <Link to="/about" data-testid="footer-link-about" className="text-cream/80 transition-colors hover:text-gold">
                  About SHi
                </Link>
              </li>
              <li>
                <a href={`mailto:${PRESS_EMAIL}?subject=Press%20%26%20Bookings`} data-testid="footer-link-press" className="text-cream/80 transition-colors hover:text-gold">
                  Press &amp; Bookings
                </a>
              </li>
              <li>
                <Link to="/about#partnerships" data-testid="footer-link-partner" className="text-cream/80 transition-colors hover:text-gold">
                  Partner with SHi
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="text-sm uppercase tracking-[0.22em] font-semibold text-latte">
              Watch &amp; Listen
            </p>
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-youtube-link"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold/60 px-6 py-3 text-sm font-bold text-gold transition-colors hover:bg-gold hover:text-ink"
            >
              <Youtube size={18} /> Watch on YouTube
            </a>
            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href={SPOTIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-platform-spotify"
                className="rounded-full border border-gold/50 px-4 py-2 text-sm font-semibold text-gold transition-colors hover:bg-gold hover:text-ink"
              >
                Spotify
              </a>
              <span
                data-testid="footer-platform-apple-podcasts"
                className="rounded-full border border-corduroy px-4 py-2 text-sm font-semibold text-latte"
              >
                Apple Podcasts &mdash; soon
              </span>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={SOCIALS.youtube} target="_blank" rel="noopener" data-testid="footer-social-youtube" aria-label="YouTube" title="YouTube" className={socialCls}>
                <Youtube size={16} />
              </a>
              <a
                href={SOCIALS.instagram}
                target="_blank"
                rel="noopener"
                data-testid="footer-social-instagram"
                aria-label="Instagram"
                title="Instagram"
                className={socialCls}
              >
                <Instagram size={16} />
              </a>
              <a
                href={SOCIALS.tiktok}
                target="_blank"
                rel="noopener"
                data-testid="footer-social-tiktok"
                aria-label="TikTok"
                title="TikTok"
                className={socialCls}
              >
                <TikTokIcon />
              </a>
              <a
                href={SOCIALS.threads}
                target="_blank"
                rel="noopener"
                data-testid="footer-social-threads"
                aria-label="Threads"
                title="Threads"
                className={socialCls}
              >
                <ThreadsIcon />
              </a>
              <a
                href={SOCIALS.twitter}
                target="_blank"
                rel="noopener"
                data-testid="footer-social-x"
                aria-label="X"
                title="X"
                className={socialCls}
              >
                <Twitter size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-20 overflow-hidden" aria-hidden="true">
          <p className="whitespace-nowrap font-display font-black uppercase leading-[0.85] tracking-tight text-cream/10 text-[13vw] select-none">
            That&rsquo;s What <span className="italic text-neon/25">SHi</span> Said
          </p>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-corduroy pt-8 text-sm text-latte md:flex-row md:items-center md:justify-between">
          <p>&copy; 2026 That&rsquo;s What SHi Said. All rights reserved.</p>
          <p className="font-editorial italic text-base text-gold/80">
            Let&rsquo;s Elaborate Wednesdays. The Program Saturdays.
          </p>
        </div>
      </div>
    </footer>
  );
}
